import React, { useMemo, useState } from 'react';
import { AccountActivityStatus } from '../types';

type Props = {
  activities: AccountActivityStatus[];
  maxActivities?: number;
};

const activityLabel = (t: AccountActivityStatus['activityType']) => {
  switch (t) {
    case 'ACCOUNT_MONITORING':
      return 'Monitoring';
    case 'POSITION_GENERATION':
      return 'Position generation';
    case 'POSITION_REVIEW':
      return 'Position review';
    case 'REBALANCING':
      return 'Rebalancing';
    case 'ORDER_PROCESSING':
      return 'Order processing';
    default:
      return t;
  }
};

const statusPillClasses = (status: AccountActivityStatus['status']) => {
  switch (status) {
    case 'in_progress':
      return 'bg-blue-100 text-blue-800 border-blue-200';
    case 'failed':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'completed':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const ActivityStatusIndicator: React.FC<Props> = ({
  activities,
  maxActivities = 3,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const visible = useMemo(() => {
    const sorted = [...(activities || [])].sort((a, b) => {
      if (a.status !== b.status) {
        // in_progress first, then failed, then completed
        const order = { in_progress: 0, failed: 1, completed: 2 } as const;
        return order[a.status] - order[b.status];
      }
      return new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime();
    });
    return sorted.slice(0, maxActivities);
  }, [activities, maxActivities]);

  if (!visible || visible.length === 0) return null;

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3">
      <div className="text-xs font-medium text-gray-700 mb-2">
        Current activity
      </div>
      <div className="space-y-2">
        {visible.map((a) => {
          const key = `${a.activityType}:${a.startedAt}`;
          const isExpanded = !!expanded[key];
          const hasDetails =
            !!a.message || (a.progress !== null && a.progress !== undefined);

          return (
            <div key={key} className="rounded border border-gray-200 bg-white">
              <button
                type="button"
                onClick={() =>
                  hasDetails &&
                  setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))
                }
                className={`w-full text-left px-3 py-2 flex items-center justify-between ${hasDetails ? 'hover:bg-gray-50' : ''}`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {activityLabel(a.activityType)}
                  </span>
                  <span
                    className={`shrink-0 inline-flex items-center px-2 py-0.5 text-xs font-medium rounded border ${statusPillClasses(a.status)}`}
                  >
                    {a.status === 'in_progress'
                      ? 'Running'
                      : a.status === 'failed'
                        ? 'Failed'
                        : 'Done'}
                  </span>
                  {typeof a.progress === 'number' && (
                    <span className="text-xs text-gray-500 shrink-0">
                      {Math.round(a.progress)}%
                    </span>
                  )}
                </div>
                {hasDetails && (
                  <span className="text-xs text-gray-500">
                    {isExpanded ? 'Hide' : 'Details'}
                  </span>
                )}
              </button>

              {hasDetails && isExpanded && (
                <div className="px-3 pb-3">
                  {typeof a.progress === 'number' && (
                    <div className="mt-2">
                      <div className="h-2 w-full bg-gray-100 rounded">
                        <div
                          className="h-2 bg-primary-600 rounded"
                          style={{
                            width: `${Math.max(0, Math.min(100, a.progress))}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {a.message && (
                    <div className="mt-2 text-sm text-gray-700">{a.message}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};


